import React from 'react';
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { axisTick, chart, tooltipStyle } from './chartTheme';

/**
 * Line / area composed chart. Ported verbatim from
 * D:\campaigning-system\src\components\charts\LineAreaChart.tsx (types stripped).
 * `series`: Array<{ key, name, color, area?, bar? }>. A `bar` series draws each bucket on its own,
 * so a single day's value never implies a curve through the days around it.
 */
const compact = (v) => (v >= 1000 ? `${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}k` : `${v}`);

export function LineAreaChart({
  data,
  xKey,
  series,
  height = 240,
  yFormatter = compact,
  yWidth = 38,
  showLegend = true,
}) {
  const hasBars = series.some((s) => s.bar);
  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <defs>
          {series.map((s) => (
            <linearGradient key={s.key} id={`fill-${s.key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={s.color} stopOpacity={0.22} />
              <stop offset="100%" stopColor={s.color} stopOpacity={0.01} />
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid stroke={chart.grid} vertical={false} />
        <XAxis dataKey={xKey} tick={axisTick} tickLine={false} axisLine={{ stroke: chart.axisLine }} dy={6} />
        <YAxis tick={axisTick} tickLine={false} axisLine={false} width={yWidth} tickFormatter={yFormatter} />
        <Tooltip {...tooltipStyle} cursor={hasBars ? { fill: 'rgba(15,168,155,0.06)' } : tooltipStyle.cursor} formatter={(v) => yFormatter(v)} />
        {showLegend && series.length > 1 && (
          <Legend
            iconType="plainline"
            iconSize={14}
            wrapperStyle={{ fontSize: 12, fontFamily: 'Hanken Grotesk', color: chart.label, paddingTop: 6 }}
          />
        )}
        {series.map((s) =>
          s.bar ? (
            <Bar key={s.key} dataKey={s.key} name={s.name} fill={s.color} radius={[4, 4, 0, 0]} maxBarSize={s.maxBarSize ?? 26} />
          ) : s.area ? (
            <Area
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.name}
              stroke={s.color}
              strokeWidth={2}
              fill={`url(#fill-${s.key})`}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 2, stroke: '#fff' }}
            />
          ) : (
            <Line
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.name}
              stroke={s.color}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 2, stroke: '#fff' }}
            />
          )
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export default LineAreaChart;
